/**
 * The three things a new workspace has to do before the dashboard has anything
 * to show, and what each of them reads as on screen.
 *
 * They are in the order they have to happen in — a brand, then the plans it
 * sells, then the first client to sell one to — and the order is enforced: a
 * step is not theirs to do until everything above it is behind them, so there
 * is one thing to press at a time.
 *
 * A step still waiting its turn is drawn exactly like the others, in full
 * colour — see `SetupChecklist`, which greys nothing back. What it does not
 * have is a link, and what it says instead of an offer is the short standing
 * note in `PENDING_NOTE`: the row reports where the workspace is rather than
 * sending anyone to work that cannot be finished yet. Once the groundwork under
 * it lands, the note becomes the same kind of "+ …" offer the step above it
 * carries, and the row becomes a link.
 *
 * A step already done stays done even where a step above it is not. That case
 * is real — plans live on the My brand screen, so they can be created before
 * the brand details are saved — and un-ticking work somebody has plainly
 * finished would be a lie about their own workspace.
 *
 * A pure function of three booleans, so the card that draws them holds no rules
 * and the rules can be read in one place.
 */

import { BRAND_PLANS_PATH, ROUTES } from "../../routes/paths";

export type SetupStepId = "brand" | "plans" | "client";

/**
 * `done` is behind them and `todo` is still outstanding.
 *
 * Two states rather than three: a step waiting its turn is still a step to do,
 * and it is no longer drawn any differently from one that is ready. Whether it
 * can be pressed is `to`, which is the only place that difference lives.
 */
export type SetupStepState = "done" | "todo";

export interface SetupStep {
  id: SetupStepId;
  title: string;
  /**
   * The line under the title, and the one thing that says which state the step
   * is in as words — so the colour it is drawn in is never carrying that alone.
   *
   * On a step behind them it is "Completed". On the step whose groundwork is
   * done it is the thing to press — "+ Create now" rather than "Pending", which
   * named the state and offered nothing to do about it. All three of those
   * carry the `+`, so a step that becomes pressable reads like the one above it
   * did. On a step still waiting its turn it is the short standing note in
   * `PENDING_NOTE`, which promises nothing because there is nothing to press.
   */
  note: string;
  state: SetupStepState;
  /**
   * Where pressing it goes, and the one thing that says whether it can be
   * pressed at all. Left off for a step with nowhere useful to send them: one
   * already done, and one whose groundwork is still outstanding.
   */
  to?: string;
}

/** What the three steps are worked out from. */
export interface SetupProgress {
  /** A brand profile has been saved — see `useBrandSetUp`. */
  brandSetUp: boolean;
  /** The workspace sells at least one plan — see `usePlansCreated`. */
  plansCreated: boolean;
  /** The workspace has at least one client, from the dashboard's own count. */
  hasClients: boolean;
  /**
   * Whether the product is shut — no plan running and no trial either, which is
   * the workspace that may read what it has and add nothing to it.
   *
   * It reaches this list because "Add first client" is a way into the Add
   * client screen like any other, and a checklist that still sent somebody
   * there would be the one entry point the gate forgot. Optional and false by
   * default: the ordering above is what this function is mostly about, and a
   * caller that has no billing answer to give — the checklist's own tests among
   * them — should get the open list.
   *
   * Only the client step. A brand and the plans it sells are set up before
   * anything is bought and are not gated anywhere else in the app either.
   */
  createLocked?: boolean;
}

/**
 * What a step waiting its turn reads as: short, and naming the state rather
 * than issuing an order it cannot follow yet. No `+`, because that prefix is
 * what marks the one row on the list that can actually be pressed.
 *
 * Plans wait on one thing only, so theirs can name it. The client step waits
 * on either or both, so it stays generic rather than picking one to mention.
 */
const PENDING_NOTE: Record<"plans" | "client" | "subscription", string> = {
  plans: "Setup brand",
  client: "Setup pending",
  /**
   * The client step on a workspace whose turn it is but whose product is shut.
   * Named for the one thing standing in the way, because unlike the two above
   * it there is nothing on this list that will clear it — the plan is bought on
   * another screen, and the row is reporting that rather than sending them
   * anywhere. The banner over the dashboard is what carries the way onto one.
   */
  subscription: "Needs a plan",
};

/**
 * The checklist, as the welcome card shows it.
 *
 * "Create plans" lands on the Plans tab of My brand, which is the only
 * place a plan is made — not on the brand details beside it.
 */
export const setupSteps = ({
  brandSetUp,
  plansCreated,
  hasClients,
  createLocked = false,
}: SetupProgress): SetupStep[] => {
  // Whether each step is theirs to do, which is the whole of the gate: every
  // step above it has to be behind them first.
  const plansReady = brandSetUp;
  // Two different reasons the client step cannot be pressed, kept apart because
  // they are answered with different words: its turn has not come, or it has
  // and there is no plan behind the workspace. The order comes first — it is
  // this list's own rule, and telling somebody they need a plan for work that
  // was not theirs to do yet names the wrong obstacle.
  const clientTurn = brandSetUp && plansCreated;
  const clientReady = clientTurn && !createLocked;

  return [
    {
      id: "brand",
      title: "Setup your brand",
      // Nothing above it, so it never waits its turn: this is where a new
      // workspace starts.
      note: brandSetUp ? "Completed" : "+ Setup now",
      state: brandSetUp ? "done" : "todo",
      to: brandSetUp ? undefined : ROUTES.myBrand,
    },
    {
      id: "plans",
      title: "Create plans",
      note: plansCreated
        ? "Completed"
        : plansReady
        ? "+ Create now"
        : PENDING_NOTE.plans,
      state: plansCreated ? "done" : "todo",
      to: !plansCreated && plansReady ? BRAND_PLANS_PATH : undefined,
    },
    {
      id: "client",
      title: "Add first client",
      note: hasClients
        ? "Completed"
        : !clientTurn
        ? PENDING_NOTE.client
        : createLocked
        ? PENDING_NOTE.subscription
        : "+ Add now",
      state: hasClients ? "done" : "todo",
      to: !hasClients && clientReady ? ROUTES.addClient : undefined,
    },
  ];
};

/** Whether there is anything left to do, which is what retires the checklist. */
export const isSetupComplete = (steps: SetupStep[]): boolean =>
  steps.every((step) => step.state === "done");
